import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@tellescope/tanstack-query";
import { i18n } from "@tellescope/i18n";
import {
  applyMongoFilter,
  compileFreeformFilterBlock,
  createKeyMap,
  validateEmail,
  validatePhone,
  validatePostalCode,
  type FreeformFieldOption,
  type FreeformFilterBlock,
} from "@tellescope/utils";
import { BlockProps } from "../types";
import { useBlock } from ".";
import { CustomFieldValue } from "../components/blocks/information/FieldsBlock/FieldCell/CustomFieldEditor";
import { getCustomFieldDisplay } from "../components/blocks/information/getCustomFieldDisplay";

export interface UseFieldsBlockArgs extends BlockProps {}

// Editor kinds a rendered field cell knows how to draw. Standard fields map to a specialized
// editor; every custom field uses the shared "custom" editor.
export type FieldEditorKind = "text" | "date" | "state" | "readonly" | "custom";

export interface FieldDescriptor {
  key: string;
  name: string;
  value?: string | null;
  displayValue: string;
  required?: boolean;
  editorKind: FieldEditorKind;
  // Present only for custom fields; drives the shared CustomFieldEditor.
  customType?: string;
  customOptions?: string[];
  rawValue?: CustomFieldValue;
}

const validators: Record<string, (value: string) => string | undefined> = {
  email: (value) => (value && !validateEmail(value) ? i18n.t("Invalid email address", { ns: "contacts" }) : undefined),
  phone: (value) => (value && !validatePhone(value) ? i18n.t("Invalid phone number", { ns: "contacts" }) : undefined),
  zipCode: (value) =>
    value && validatePostalCode(value).error ? i18n.t("Please enter a valid zip code", { ns: "contacts" }) : undefined,
};

export const useFieldsBlock = ({
  enduserId,
  info,
  editMode: providedEditMode,
  setEditMode: providedSetEditMode,
  viewId,
  blockId,
  blockIndex,
  blockLayout,
}: UseFieldsBlockArgs) => {
  const { t } = useTranslation("contacts");
  const { selectedOrganization, currentUser } = useAuth();
  const [editingKey, setEditingKey] = useState<string>();
  const [editingError, setEditingError] = useState<string>();

  const block = useBlock({
    type: "information.fields",
    info,
    editMode: providedEditMode,
    setEditMode: providedSetEditMode,
    enduserId,
    viewId,
    blockId,
    blockIndex,
    blockLayout,
    select: (enduser) => enduser,
  });
  const enduser = block.enduserQuery.data;

  const fieldConfigs = useMemo(
    () => createKeyMap(selectedOrganization?.settings?.endusers?.builtinFields ?? [], "field"),
    [selectedOrganization?.settings?.endusers?.builtinFields]
  );

  const visibleCustomFields = useMemo(
    () => (selectedOrganization?.settings?.endusers?.customFields ?? []).filter((f) => !f.hiddenFromProfile),
    [selectedOrganization?.settings?.endusers?.customFields]
  );

  const isFieldHidden = useCallback((field: string) => fieldConfigs[field]?.hidden ?? false, [fieldConfigs]);

  // Two selectable filter fields (calendar "Select field" precedent): the field's name and its
  // displayed value. Each rendered field is matched as { name, value }. "name" is tags-typed so its
  // value is a set of names (multiselect, like TagsBlock); "value" is a free-text string match.
  const filterFieldOptions = useMemo<FreeformFieldOption[]>(
    () => [
      {
        value: "name",
        label: t("Field Name"),
        valueType: "tags",
        operations: ["equals", "does_not_equal", "contains", "does_not_contain"],
      },
      {
        value: "value",
        label: t("Field Value"),
        valueType: "string",
        operations: ["equals", "does_not_equal", "is_set", "is_not_set", "contains", "does_not_contain"],
      },
    ],
    [t]
  );

  const filterBlock = useMemo<FreeformFilterBlock>(
    () => block.parsedInfo?.filter ?? { id: blockId ?? `${viewId}-${blockIndex}`, logic: "and", filters: [] },
    [block.parsedInfo?.filter, blockId, viewId, blockIndex]
  );

  // Evaluated one field at a time (see fields below), so array needles use per-item semantics.
  const compiledFilter = useMemo(
    () => compileFreeformFilterBlock(filterBlock, { evalArraysByItem: true }),
    [filterBlock]
  );

  const zipValue = enduser?.zipPlusFour ? `${enduser.zipCode}-${enduser.zipPlusFour}` : enduser?.zipCode;

  const allFields = useMemo<FieldDescriptor[]>(() => {
    const standardBase: { key: string; name: string; value?: string | null; editorKind: FieldEditorKind }[] = [
      { key: "fname", name: t("First Name"), value: enduser?.fname, editorKind: "text" },
      { key: "lname", name: t("Last Name"), value: enduser?.lname, editorKind: "text" },
      { key: "email", name: t("Email"), value: enduser?.email, editorKind: "text" },
      { key: "phone", name: t("Phone"), value: enduser?.phone, editorKind: "text" },
      { key: "dateOfBirth", name: t("Date of Birth"), value: enduser?.dateOfBirth, editorKind: "date" },
      { key: "addressLineOne", name: t("Address"), value: enduser?.addressLineOne, editorKind: "text" },
      { key: "addressLineTwo", name: t("Address Line 2"), value: enduser?.addressLineTwo, editorKind: "text" },
      { key: "city", name: t("City"), value: enduser?.city, editorKind: "text" },
      { key: "state", name: t("State"), value: enduser?.state, editorKind: "state" },
      { key: "zipCode", name: t("ZIP Code"), value: zipValue, editorKind: "text" },
      {
        key: "primaryInsurance",
        name: t("Primary Insurance"),
        value: enduser?.insurance?.planName,
        editorKind: "readonly",
      },
      {
        key: "secondaryInsurance",
        name: t("Secondary Insurance"),
        value: enduser?.insuranceSecondary?.planName,
        editorKind: "readonly",
      },
    ];

    const standard = standardBase.reduce<FieldDescriptor[]>((acc, field) => {
      if (!isFieldHidden(field.key)) {
        acc.push({ ...field, required: fieldConfigs[field.key]?.required, displayValue: field.value ?? "" });
      }
      return acc;
    }, []);

    const custom: FieldDescriptor[] = visibleCustomFields.map((customField) => {
      const rawValue = enduser?.fields?.[customField.field];
      const options = customField.info && "options" in customField.info ? customField.info.options : undefined;
      return {
        key: `custom-${customField.field}`,
        name: customField.field,
        value: undefined,
        displayValue: getCustomFieldDisplay(customField.type, rawValue),
        required: customField.required,
        editorKind: "custom",
        customType: customField.type,
        customOptions: options,
        rawValue,
      };
    });

    return [...standard, ...custom];
  }, [enduser, zipValue, fieldConfigs, isFieldHidden, visibleCustomFields, t]);

  const fields = useMemo<FieldDescriptor[]>(() => {
    if (!compiledFilter) return allFields;
    return allFields.filter((field) =>
      applyMongoFilter({ name: field.name, value: field.displayValue }, compiledFilter)
    );
  }, [allFields, compiledFilter]);

  // Field-name options for the filter's multiselect value input: every field's name, plus any
  // free-form names already chosen in the filter (MultiSelect only renders values it has options for).
  const fieldNameOptions = useMemo<string[]>(() => {
    const selected = filterBlock.filters.flatMap((filter) =>
      filter.field === "name" && Array.isArray(filter.value) ? filter.value : []
    );
    return [...new Set([...allFields.map((field) => field.name), ...selected])];
  }, [allFields, filterBlock.filters]);

  const startEditing = (key: string) => {
    setEditingError(undefined);
    setEditingKey(key);
  };

  const stopEditing = () => {
    setEditingError(undefined);
    setEditingKey(undefined);
  };

  const saveField = (
    key: string,
    update: Omit<Parameters<typeof block.updateEnduserMutation.mutateAsync>[0], "id">,
    validationValue?: string
  ) => {
    const error = validationValue !== undefined ? validators[key]?.(validationValue) : undefined;
    if (error) {
      setEditingError(error);
      return;
    }
    // Skip the mutation when nothing actually changed (each updated key matches the current value).
    const current: Record<string, unknown> = { ...enduser };
    const unchanged = Object.entries(update).every(([field, next]) => (current[field] ?? "") === (next ?? ""));
    if (unchanged) {
      stopEditing();
      return;
    }
    block.updateEnduserMutation.mutateAsync(
      {
        id: enduserId,
        ...update,
        _options: {
          replaceObjectFields: true,
        },
      },
      { onSuccess: stopEditing }
    );
  };

  const saveCustomField = (field: string, value: CustomFieldValue) => {
    const nextValue = value ?? null;
    const currentValue = enduser?.fields?.[field] ?? null;
    if (JSON.stringify(currentValue) === JSON.stringify(nextValue)) {
      stopEditing();
      return;
    }
    block.updateEnduserMutation.mutateAsync(
      {
        id: enduserId,
        fields: { [field]: nextValue },
        _options: { replaceObjectFields: typeof nextValue === "object" ? true : false },
      },
      { onSuccess: stopEditing }
    );
  };

  const onFilterChange = useCallback(
    (nextBlock: FreeformFilterBlock) => {
      block.updateInfo({ ...block.parsedInfo, id: block.parsedInfo?.id ?? crypto.randomUUID(), filter: nextBlock });
    },
    [block]
  );

  return {
    ...block,
    t,
    enduser,
    fields,
    filterBlock,
    filterFieldOptions,
    fieldNameOptions,
    onFilterChange,
    currentUserId: currentUser?.id,
    editingKey,
    editingError,
    startEditing,
    stopEditing,
    saveField,
    saveCustomField,
  };
};
